import { Component } from 'react';
import { Base, confirm, t } from 'zf-web-components';
import { reporter } from 'zf-web-infra';
import { Exception } from '../error';
import { ErrorHandler } from './handler';
import type { ErrorBoundaryProps, ErrorBoundaryState } from './types';
import type { ReactNode, ErrorInfo } from 'react';

// TODO: 共通
/**
 * 错误边界，处理的错误类型包括：
 * 1. 渲染错误
 * 2. 同步方法 & 异步方法 & 资源加载 异常
 * 3. promise & async/await 异常
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {

  private readonly handler: ErrorHandler;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };

    this.handler = new ErrorHandler();
    this.catchError = this.catchError.bind(this);
    this.catchRejectEvent = this.catchRejectEvent.bind(this);
  }

  /**
   * 在componentDidCatch里格式化后，再显示异常内容
   *
   * 用于在组件的子组件抛出错误后捕获并处理错误，
   * 通常使用该方法来显示一个错误信息，以便用户知道发生了错误。
   * getDerivedStateFromError会先被调用，用于更新组件的状态，然后，componentDidCatch会被调用
   */
  // static getDerivedStateFromError(): Pick<ErrorBoundaryState, 'hasError'> {
  //   return { hasError: true };
  // }

  componentDidMount(): void {
    window.addEventListener('error', this.catchError, true);
    window.addEventListener('unhandledrejection', this.catchRejectEvent, true);
  }

  componentWillUnmount(): void {
    window.removeEventListener('error', this.catchError, true);
    window.removeEventListener('unhandledrejection', this.catchRejectEvent, true);
  }

  /**
   * 捕获「渲染错误」
   * 用于捕获并处理组件的子组件抛出的错误，通常在该方法中可以记录错误日志或发送错误报告等操作
   *
   * 这种异常通常不会出现，跳转到Error页面
   */
  componentDidCatch(error: Error, info: ErrorInfo): void {
    const parsed = this.handler.handleRenderError(error, info);
    if (parsed.type === 'ignore') {
      return;
    }

    reporter.captureException(error);
    this.setState({ error: parsed });
  }

  /**
   * 捕获「同步方法 & 异步方法 & 资源加载 异常」
   *
   * Error页面过于隆重，这种异常只用alert方式提示
   */
  private catchError(error: ErrorEvent): void {
    // 这里仅上报错误，路由的 errorElement 会显示错误页面
    reporter.captureException(error.error);
    return;
    // const parsed = this.handler.handleUncaughtError(error);
    // if (parsed.type === 'ignore') {
    //   return;
    // }

    // reporter.captureException(error);
    // return this.alert(parsed.formatted ?? parsed.message);
  }

  /**
   * 捕获「promise & async/await 异常」
   */
  private catchRejectEvent(error: PromiseRejectionEvent): void {
    const parsed = this.handler.handleRejectError(error);
    if (parsed.type === 'ignore') {
      return;
    }
    if (parsed.type === 'alert') {
      return this.alert(parsed.formatted ?? parsed.message);
    }
    if (parsed.type === 'confirm') {
      return this.confirm(parsed.formatted ?? parsed.message);
    }

    reporter.captureException(error);
    this.setState({ error: parsed });
  }

  private alert(error: string | string[]): void {
    Base.message.error(error);
  }

  private confirm(content: string | string[]): void {
    confirm({ title: t('错误'), content });
  }

  render(): ReactNode {
    if (this.state.error != null) {
      return <Exception error={this.state.error} />;
    }

    return this.props.children;
  }

}
